import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

export interface Language {
	code: string;
	name: string;
}

@Injectable({
	providedIn: 'root',
})
export class LanguageService {
	private readonly DEFAULT_LANGUAGE = 'zh-tw';

	// 語系變更事件
	language$ = new ReplaySubject<LangChangeEvent>(1);

	// 支援的語系清單
	private readonly supportedLanguages: Language[] = [
		{ code: 'zh-tw', name: '繁體中文' },
		{ code: 'en-us', name: 'English' },
	];

	private readonly isBrowser: boolean;

	constructor(
		private readonly translateService: TranslateService,
		private readonly router: Router,
		@Inject(PLATFORM_ID) private platformId: Object,
	) {
		this.isBrowser = isPlatformBrowser(this.platformId);
	}

	/**
	 * 頁面初始化執行的 function
	 */
	setInitState() {
		// 支援的語系
		this.translateService.addLangs(
			this.supportedLanguages.map(lang => lang.code),
		);

		// 設定預設語系為中文
		this.translateService.setDefaultLang(this.DEFAULT_LANGUAGE);
		this.setLang(this.DEFAULT_LANGUAGE);
		this.initLanguageNavigationMonitor();
	}

	/** 當語系變更為英文時，導航到首頁 */
	private initLanguageNavigationMonitor() {
		if (!this.isBrowser) {
			return;
		}

		this.translateService.onLangChange.subscribe(
			(event: LangChangeEvent) => {
				if (event.lang === 'en-us') {
					this.router.navigate(['/']);
				}
			},
		);
	}

	/** 檢查語系是否存在 */
	private isLanguageSupported(language: string): boolean {
		return this.supportedLanguages.some(lang => lang.code === language);
	}

	/** 設定目前使用的語系 */
	setLang(lang: string) {
		if (!this.isLanguageSupported(lang)) {
			return;
		}

		this.translateService.onLangChange.pipe(take(1)).subscribe(result => {
			this.language$.next(result);
		});
		this.translateService.use(lang);
	}

	/** 取得目前語系 */
	getCurrentLang(): string {
		return this.translateService.currentLang || this.DEFAULT_LANGUAGE;
	}

	/** 取得語系列表 */
	getSupportedLanguages(): Language[] {
		return this.supportedLanguages;
	}

	/** 取得語系顯示名稱 */
	getLanguageDisplayName(langCode: string): string {
		const language = this.supportedLanguages.find(
			lang => lang.code === langCode,
		);
		return language ? language.name : langCode;
	}

	/** 監聽語系變更 */
	get onLanguageChange() {
		return this.translateService.onLangChange;
	}
}
